import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { ContentLoading, ErrorPage } from 'axway-react-ui';
import browserHistory from 'components/utility/history';
import * as restartActions from 'components/widgets/Restart/actions';
import ComponentsListView from '../view/ComponentsListView';
import OperationView from '../view/OperationView';
import PluginInUseView from '../view/PluginInUseView';
import UninstallConfirmView from '../view/UninstallConfirmView';
import InstallConfirmView from '../view/InstallConfirmView';
import * as actions from '../actions';

export class ComponentsList extends React.Component {
	constructor(props) {
		super(props);
		this.installComponent = this.installComponent.bind(this);
		this.installRequest = this.installRequest.bind(this);
		this.uninstallComponent = this.uninstallComponent.bind(this);
		this.uninstallRequest = this.uninstallRequest.bind(this);
		this.cancelRequest = this.cancelRequest.bind(this);
		this.clearInstall = this.clearInstall.bind(this);
		this.installCancel = this.installCancel.bind(this);
		this.renderCommandModal = this.renderCommandModal.bind(this);
		this.awaitRestart = this.awaitRestart.bind(this);
		this.state = {
			pluginToUninstall: null,
			pluginToInstall: null
		};
	}

	installRequest(component) {
		// Only set state and re-render if we need to display the confirmation
		if (component.tags.includes('community')) {
			return this.setState({
				pluginToInstall: component
			});
		}
		// Otherwise directly install the plugin
		this.props.actions.startOperation(component.package, 'install');
	}

	installComponent() {
		this.props.actions.startOperation(this.state.pluginToInstall.package, 'install');
		this.setState({
			pluginToInstall: null
		});
	}

	uninstallRequest(component) {
		this.setState({
			pluginToUninstall: component
		});
	}

	uninstallComponent() {
		this.props.actions.startOperation(this.state.pluginToUninstall.package, 'uninstall');
		this.setState({
			pluginToUninstall: null
		});
	}

	cancelRequest() {
		this.setState({
			pluginToUninstall: null,
			pluginToInstall: null
		});
	}

	installCancel() {
		this.props.actions.installCancel();
	}

	awaitRestart() {
		this.props.restartActions.awaitRestart({ showDialog: false });
	}

	clearInstall(ev, configFile) {
		this.props.actions.clearInstall();
		// TODO: move into install saga by dispatching LOCATION event on complete
		// install when configFile is produced. A bit more explicit than checking for
		// a redirect whenever the modal is closed
		if (configFile) {
			// Navigate to edit the config file
			browserHistory.push(`${window.$conf.prefix}/project/configuration/${configFile}`);
		}
	}

	/**
	 * Renders the Install ModalDialog.
	 */
	renderCommandModal() {
		const processingInfo = this.props.processing;
		if (!processingInfo) {
			return null;
		}

		return (
			<OperationView
				componentName={processingInfo.name}
				status={processingInfo.status}
				configFile={processingInfo.configFile}
				log={processingInfo.log}
				operation={processingInfo.operation}
				onClose={this.clearInstall}
				onInstallCancel={this.installCancel}
				onRetryRestart={this.awaitRestart}
			/>
		);
	}

	renderUninstallConfirmationModal() {
		const plugin = this.state.pluginToUninstall;
		if (!plugin) {
			return;
		}

		if (plugin.usage.uninstallable) {
			// Model autogen can be Array with connector names or null
			return (
				<UninstallConfirmView
					connectorsWithModelAutogen={plugin.usage.modelAutogen}
					cancelUninstallRequest={this.cancelRequest}
					uninstallComponent={this.uninstallComponent}
				/>
			);
		}
		return (
			<PluginInUseView
				plugin={plugin}
				onClose={this.cancelRequest}
			/>
		);
	}

	renderInstallConfirmationModel() {
		const plugin = this.state.pluginToInstall;
		if (!plugin) {
			return;
		}

		return (
			<InstallConfirmView
				cancelInstallRequest={this.cancelRequest}
				installComponent={this.installComponent}
			/>
		);
	}

	render() {
		const { components, status } = this.props;
		if (status === 'error') {
			return (
				<ErrorPage errorDetail={this.props.errorDetail} />
			);
		}

		return (
			<div>
				{this.renderUninstallConfirmationModal()}
				{this.renderInstallConfirmationModel()}
				{this.renderCommandModal()}
				{status === 'loading' ? (
					<ContentLoading />
				) : (
					<ComponentsListView
						items={components}
						onInstallRequest={this.installRequest}
						onUninstallRequest={this.uninstallRequest}
					/>
				)}
			</div>
		);
	}
}

ComponentsList.propTypes = {
	status: PropTypes.string,
	errorDetail: PropTypes.object,
	components: PropTypes.array,
	processing: PropTypes.shape({
		operation: PropTypes.string.isRequired,
		status: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		configFile: PropTypes.string,
		log: PropTypes.string
	}),
	operation: PropTypes.string
};

function mapStateToProps(state) {
	return {
		components: state.components.components,
		status: state.components.status,
		errorDetail: state.components.errorDetail,
		processing: state.components.processing,
		operation: state.components.operation
	};
}

function mapDispatchToProps(dispatch) {
	return {
		actions: bindActionCreators(actions, dispatch),
		restartActions: bindActionCreators(restartActions, dispatch)
	};
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ComponentsList);
